import { DateTime } from 'luxon';
import { cn } from '@Utils/cn';
import type {
  EventReportSummary,
  EventReportVerdict,
} from '@Types/eventReport';

function dayLabel(iso: string): string {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return '';
  const cal = dt.toRelativeCalendar(); // "today" | "yesterday" | "3 days ago"
  const label = cal ? cal.charAt(0).toUpperCase() + cal.slice(1) : '';
  return `${label} · ${dt.toFormat('t')}`;
}

const VERDICT_META: Record<
  EventReportVerdict,
  { label: string; className: string }
> = {
  SELLOUT: { label: 'Sold out', className: 'bg-emerald-100 text-emerald-700' },
  STRONG: { label: 'Strong', className: 'bg-green-100 text-green-700' },
  SOFT: { label: 'Soft', className: 'bg-amber-100 text-amber-700' },
  UNDERSOLD: { label: 'Undersold', className: 'bg-rose-100 text-rose-700' },
  NO_CAPACITY: { label: 'No capacity', className: 'bg-zinc-100 text-zinc-600' },
};

interface Props {
  report: EventReportSummary;
  onOpen: () => void;
}

export default function EventReportCard({ report, onOpen }: Props) {
  const verdict = VERDICT_META[report.status] ?? VERDICT_META.NO_CAPACITY;
  const soldLabel = report.capacity
    ? `${report.ticketsSold}/${report.capacity}`
    : `${report.ticketsSold} sold`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'block w-full rounded-xl border border-zinc-200 bg-white p-4 text-left shadow-sm transition',
        'hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-md',
        report.isNew && 'agent-shimmer ring-1 ring-amber-200',
      )}
    >
      <div className="flex items-center justify-between gap-3 text-xs text-zinc-500">
        <span className="flex items-center gap-2">
          <span className="font-medium text-zinc-600">
            {report.eventName} · {soldLabel}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase',
              verdict.className,
            )}
          >
            {verdict.label}
          </span>
          {report.isNew && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-amber-700 uppercase">
              New
            </span>
          )}
        </span>
        <span className="whitespace-nowrap">{dayLabel(report.eventDate)}</span>
      </div>

      {/* the report's headline, framed as one of the agent's thoughts */}
      <p className="mt-2 flex gap-2 text-sm leading-relaxed text-zinc-800">
        <span aria-hidden className="mt-0.5 shrink-0">
          💭
        </span>
        <span>{report.highlight}</span>
      </p>
    </button>
  );
}
